import { MEMBERS_TABS, type BreadcrumbNavKey } from "breadcrumbsConfig";
import {
  PROJECT_LINKS,
  PROJECT_STEPS,
  buildProjectBasePath,
  buildProjectHref,
  getProjectStepItemSegment,
} from "config/navigation";
import { SETTINGS_SECTIONS } from "pages/settings/Settings.sections";
import { WORKSPACE_SETTINGS_SECTIONS } from "pages/settings/workspace/WorkspaceSettings.sections";

export type BreadcrumbDropdownItem = { label: string; href: string };

const WORKSPACE_SETTINGS_PATH_PATTERN = /^\/workspaces\/[^/]+\/settings(\/|$)/;
const ACCOUNT_SETTINGS_PATH_PATTERN = /^\/settings(\/|$)/;
const PROJECT_PATH_PATTERN = /^\/workspaces\/[^/]+\/projects\/[^/]+(\/|$)/;

export const isWorkspaceSettingsPath = (pathname: string): boolean =>
  WORKSPACE_SETTINGS_PATH_PATTERN.test(pathname);

export const isAccountSettingsPath = (pathname: string): boolean =>
  ACCOUNT_SETTINGS_PATH_PATTERN.test(pathname);

export const isProjectPath = (pathname: string): boolean => PROJECT_PATH_PATTERN.test(pathname);

const SECURITY_PATH_PREFIX = "/settings/security/";

/**
 * Sibling sections for personal-settings crumbs, derived from the same
 * SETTINGS_SECTIONS the sidebar renders so the two can't drift.
 */
export const getAccountSettingsDropdownItems = (
  crumbIndex: number,
  crumbs: Array<{ label: string }>,
  currentCrumb: { label: string }
): BreadcrumbDropdownItem[] => {
  const toItem = (section: { title: string; path: string }): BreadcrumbDropdownItem => ({
    label: section.title,
    href: section.path,
  });

  const parentCrumb = crumbs[crumbIndex - 1];

  const securitySections = SETTINGS_SECTIONS.filter((section) =>
    section.path.startsWith(SECURITY_PATH_PREFIX)
  );

  if (parentCrumb?.label === "Security") {
    if (securitySections.some((section) => section.title === currentCrumb.label)) {
      return securitySections.filter((section) => section.title !== currentCrumb.label).map(toItem);
    }
    return [];
  }

  if (crumbIndex === 1) {
    const topLevelSections = SETTINGS_SECTIONS.filter(
      (section) => !section.path.startsWith(SECURITY_PATH_PREFIX)
    );
    if (
      topLevelSections.some((section) => section.title === currentCrumb.label) ||
      currentCrumb.label === "Security"
    ) {
      return topLevelSections.filter((section) => section.title !== currentCrumb.label).map(toItem);
    }
  }

  return [];
};

export interface NavCrumbContext {
  workspaceId?: string;
  projectId?: string;
}

/**
 * Siblings of a crumb, found by its id in the navigation config. Every label
 * and link comes from the same config the sidebar renders.
 */
export function getNavCrumbSiblings(
  nav: BreadcrumbNavKey,
  { workspaceId, projectId }: NavCrumbContext
): BreadcrumbDropdownItem[] {
  if (!workspaceId) return [];

  switch (nav.kind) {
    case "step": {
      if (!projectId) return [];
      return PROJECT_STEPS.filter((step) => step.id !== nav.stepId).map((step) => ({
        label: step.label,
        href: buildProjectHref(
          workspaceId,
          projectId,
          getProjectStepItemSegment(step, step.items[0])
        ),
      }));
    }
    case "step-item": {
      if (!projectId) return [];
      const step = PROJECT_STEPS.find((candidate) => candidate.id === nav.stepId);
      if (!step) return [];
      return step.items
        .filter((item) => item.id !== nav.itemId)
        .map((item) => ({
          label: item.label,
          href: buildProjectHref(workspaceId, projectId, getProjectStepItemSegment(step, item)),
        }));
    }
    case "project-link": {
      if (!projectId) return [];
      return PROJECT_LINKS.filter((link) => link.id !== nav.linkId).map((link) => ({
        label: link.label,
        href: buildProjectHref(workspaceId, projectId, link.segment),
      }));
    }
    case "workspace-section":
      return WORKSPACE_SETTINGS_SECTIONS.filter((section) => section.id !== nav.sectionId).map(
        (section) => ({
          label: section.title,
          href: section.path.replace(":workspaceId", workspaceId),
        })
      );
    case "members-tab": {
      const base =
        nav.scope === "project"
          ? projectId
            ? `${buildProjectBasePath(workspaceId, projectId)}/settings/members`
            : null
          : `/workspaces/${workspaceId}/settings/members`;
      if (!base) return [];
      return MEMBERS_TABS.filter(({ tab }) => tab !== nav.tab).map(({ tab, label }) => ({
        label,
        href: `${base}/${tab}`,
      }));
    }
    default:
      return [];
  }
}
